/**
 * チバリヨ - 顧客の訪問実績集計 (日報から導出)
 *
 * 背景:
 *   顧客マスタ(clients)の lastVisitDate / visitCount は日報作成時にライブ更新
 *   されない (CSV/移行時は null/0 据え置き、増分処理が存在しない)。このため
 *   P1/P7 の「訪問途絶 / 訪問記録なし」判定が実態とずれていた。
 *
 *   本モジュールは reports (type==='訪問') を集計して顧客ごとの実際の
 *   最終訪問日・訪問回数を求め、表示側の client オブジェクトに上書き反映する。
 *   保存済みフィールドには書き込まない (表示時計算のみ。ルール/書き込み増なし)。
 *
 * 訪問の定義:
 *   type === '訪問' の日報のみをカウント (seed-reports-dev.js / KPI と同基準)。
 *   client.clientId で顧客マスタに紐づく日報のみ対象 (フリーテキスト入力で
 *   clientId が無い日報は集計対象外)。
 *
 * 規模:
 *   reports 全件 getDocs (数百件規模)。P7 の全期間日報検索と同じ全件走査方式。
 */

import {
  collection,
  getDocs,
} from 'https://www.gstatic.com/firebasejs/10.13.0/firebase-firestore.js';

import { db } from './firebase-init.js';

/**
 * 日報(type==='訪問')から顧客ごとの最終訪問日・訪問回数を集計する。
 * @returns {Promise<Map<string, { lastVisitDate: string, visitCount: number }>>}
 */
export async function fetchVisitStatsByClient() {
  const snap = await getDocs(collection(db, 'reports'));
  const map = new Map();

  snap.forEach((d) => {
    const r = d.data();
    if (r.type !== '訪問') return;
    const cid = r.client && r.client.clientId;
    if (!cid) return;
    const date = typeof r.date === 'string' ? r.date : '';

    const cur = map.get(cid) || { lastVisitDate: '', visitCount: 0 };
    cur.visitCount += 1;
    if (date && date > cur.lastVisitDate) cur.lastVisitDate = date;
    map.set(cid, cur);
  });

  return map;
}

/**
 * 集計値を client オブジェクト配列に上書き反映する (破壊的)。
 * 訪問日報が1件以上ある顧客のみ上書き。無い顧客は保存済みの値を維持する。
 * @param {Array<object>} clients - id を持つ client オブジェクト配列
 * @param {Map} statsMap - fetchVisitStatsByClient() の返り値
 */
export function applyVisitStats(clients, statsMap) {
  if (!Array.isArray(clients) || !statsMap) return;
  for (const c of clients) {
    const s = statsMap.get(c.id);
    if (s && s.visitCount > 0) {
      c.lastVisitDate = s.lastVisitDate || null;
      c.visitCount = s.visitCount;
    }
  }
}
